"use client";

import Link from "next/link";
import { Music2, Guitar, Mic, Music } from "lucide-react";

interface CategoryCardProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  href?: string;
}

const categories: CategoryCardProps[] = [
  { icon: Music2, label: "클래식 악기", href: "/list?category=classic" },
  { icon: Guitar, label: "실용음악/밴드", href: "/list?category=band" },
  { icon: Mic, label: "음향·레코딩", href: "/list?category=audio" },
  { icon: Music, label: "국악기", href: "/list?category=traditional" },
];

export default function CategorySection() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-[#1f2850] via-[#252f5a] to-[#2c3a7b] text-white">
      {/* 미묘한 배경 장식 */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 blur-3xl rounded-full" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/10 blur-3xl rounded-full" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-24 py-16 sm:py-20 lg:py-24">
        {/* 섹션 헤더 */}
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-[-0.02em] mb-4">
            <span className="bg-gradient-to-r from-white via-blue-100 to-indigo-100 bg-clip-text text-transparent">
              전국의 모든 악기, 한 곳에서.
            </span>
          </h2>
        </div>

        {/* 카테고리 카드 그리드 */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {categories.map((category, index) => (
            <CategoryCard key={index} {...category} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CategoryCard({ icon: Icon, label, href = "/list" }: CategoryCardProps) {
  const CardContent = (
    <div className="group relative h-full w-full rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 sm:p-8 lg:p-10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl hover:shadow-[#7A56E8]/20 cursor-pointer">
      {/* 카드 배경 그라데이션 (hover 시) */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#7A56E8]/0 to-[#6D46D1]/0 group-hover:from-[#7A56E8]/10 group-hover:to-[#6D46D1]/10 transition-all duration-300 opacity-0 group-hover:opacity-100" />

      {/* 콘텐츠 */}
      <div className="relative flex flex-col items-center text-center space-y-4">
        {/* 아이콘 */}
        <div className="p-4 rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 group-hover:from-[#7A56E8]/20 group-hover:to-[#6D46D1]/20 group-hover:border-[#7A56E8]/30 transform group-hover:scale-110 transition-all duration-300">
          <Icon className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 text-white group-hover:text-white/90" strokeWidth={2} />
        </div>

        {/* 라벨 */}
        <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-white/90 group-hover:text-white transition-colors tracking-tight">
          {label}
        </h3>
      </div>
    </div>
  );

  if (href) {
    return (
      <Link href={href} className="block h-full">
        {CardContent}
      </Link>
    );
  }

  return CardContent;
}

